import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { AlertTriangle, Check } from "lucide-react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Stepper>. State
// derivation (`stateOf`) and CSS ported letter-for-letter from the
// vendor's compiled bundle and its Stepper CSS — new .rc-stepper class
// names. `current === steps.length` (used 3x in this app to mean "all
// steps complete, none current") needs no special-casing: `idx < current`
// is true for every index when `current` is past the last one, so every
// step naturally resolves to "complete" already. Icon switched to
// lucide's Check, same reasoning as Checkbox.
export type StepperOrientation = "horizontal" | "vertical";
export type StepState = "complete" | "current" | "upcoming" | "error";

export interface StepperStep {
  title: ReactNode;
  description?: ReactNode;
  error?: boolean;
}

export interface StepperProps extends Omit<HTMLAttributes<HTMLOListElement>, "children"> {
  steps: StepperStep[];
  current: number;
  orientation?: StepperOrientation;
  "aria-label"?: string;
}

function stateOf(idx: number, current: number, error: boolean): StepState {
  if (error) return "error";
  if (idx < current) return "complete";
  if (idx === current) return "current";
  return "upcoming";
}

export const Stepper = forwardRef<HTMLOListElement, StepperProps>(function Stepper(
  { steps, current, orientation = "horizontal", className, "aria-label": ariaLabel, ...rest },
  ref
) {
  return (
    <ol ref={ref} data-orientation={orientation} aria-label={ariaLabel} className={cn("rc-stepper", className)} {...rest}>
      {steps.map((step, idx) => {
        const state = stateOf(idx, current, Boolean(step.error));
        const isLast = idx === steps.length - 1;
        const ariaCurrent = state === "current" ? "step" : undefined;
        return (
          <li key={idx} data-state={state} aria-current={ariaCurrent} className="rc-stepper__step">
            <div className="rc-stepper__marker">
              {/* Phase 4.9 — a failed step was red and NOTHING else: same
                  circle, same number, just a different hue. That is
                  color-alone signalling (WCAG 1.4.1), so it did not exist
                  for a dichromat, in high-contrast mode, or in a printed
                  onboarding screenshot. The glyph changes too now, exactly
                  as `complete` already swapped the number for a tick. */}
              <span className="rc-stepper__indicator" aria-hidden="true">
                {state === "error" ? <AlertTriangle width={13} height={13} />
                  : state === "complete" ? <Check width={14} height={14} />
                  : idx + 1}
              </span>
              {isLast ? null : <span className="rc-stepper__connector" aria-hidden="true" />}
            </div>
            <div className="rc-stepper__label">
              <span className="rc-stepper__title">
                {step.title}
                {/* And the state is said out loud, because the icon is
                    decorative and aria-current only covers "current". */}
                {state === "error" ? <span className="sr-only"> — failed</span> : null}
                {state === "complete" ? <span className="sr-only"> — completed</span> : null}
              </span>
              {step.description ? <span className="rc-stepper__description">{step.description}</span> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
});
